import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useFleetPermissions } from "@/hooks/use-fleet-permissions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/admin/data-table";
import { Badge } from "@/components/ui/badge";
import { Route, Car, MapPin, Plus, Edit2, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ColumnDef } from "@tanstack/react-table";
import { TripFormSheet } from "@/components/fleet/TripFormSheet";

interface Trip {
  id: string;
  vehicle_id: string;
  driver_id: string | null;
  origin: string | null;
  destination: string | null;
  origin_lat?: number | null;
  origin_lng?: number | null;
  destination_lat?: number | null;
  destination_lng?: number | null;
  eta_start: string | null;
  eta_end: string | null;
  start_at: string | null;
  end_at: string | null;
  status: string;
  distance_planned_km: number | null;
  distance_actual_km: number | null;
  created_at: string;
  fleet_vehicles?: { plate: string; make: string; model: string };
  fleet_drivers?: { name: string };
}

export default function FleetTrips() {
  const { profile, companies, activeCompany, hasCompanyRole } = useAuth();
  const { canCreate, canEdit, canDelete, isViewer } = useFleetPermissions();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState<Trip | null>(null);

  const currentCompanyId = activeCompany?.id || companies?.[0]?.id;

  // Fetch trips
  const { data: trips, isLoading } = useQuery({
    queryKey: ["fleetTrips", currentCompanyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fleet_trips")
        .select(`
          *,
          fleet_vehicles!vehicle_id(plate, make, model),
          fleet_drivers!driver_id(name)
        `)
        .eq("company_id", currentCompanyId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!currentCompanyId,
  });

  // Delete trip mutation
  const deleteTripMutation = useMutation({
    mutationFn: async (id: string) => {
      // Verificar permissão primeiro
      if (!hasCompanyRole(activeCompany?.id, 'company_admin')) {
        throw new Error('Você não tem permissão para excluir viagens. Apenas administradores da empresa podem realizar esta ação.');
      }

      const { error, count } = await supabase
        .from("fleet_trips")
        .delete()
        .eq("id", id);

      if (error) throw error;
      
      if (count === 0) {
        throw new Error('Viagem não encontrada ou já foi removida.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetTrips"] });
      toast({
        title: "Sucesso",
        description: "Viagem removida com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao remover viagem",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleEdit = (trip: Trip) => {
    setEditingTrip(trip);
    setIsDialogOpen(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "planned":
        return <Badge variant="outline">Planejada</Badge>;
      case "in_progress":
        return <Badge variant="default">Em Andamento</Badge>;
      case "completed":
        return <Badge variant="secondary">Concluída</Badge>;
      case "cancelled":
        return <Badge variant="destructive">Cancelada</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("pt-BR");
  };

  const columns: ColumnDef<Trip>[] = [
    {
      accessorKey: "fleet_vehicles.plate",
      header: "Veículo",
      cell: ({ row }) => {
        const vehicle = row.original.fleet_vehicles;
        return vehicle ? `${vehicle.plate} - ${vehicle.make} ${vehicle.model}` : "N/A";
      },
    },
    {
      accessorKey: "fleet_drivers.name",
      header: "Motorista",
      cell: ({ row }) => row.original.fleet_drivers?.name || "Não definido",
    },
    {
      accessorKey: "origin",
      header: "Origem",
      cell: ({ row }) => row.getValue("origin") || "N/A",
    },
    {
      accessorKey: "destination",
      header: "Destino",
      cell: ({ row }) => row.getValue("destination") || "N/A",
    },
    {
      accessorKey: "eta_start",
      header: "Início Previsto",
      cell: ({ row }) => formatDateTime(row.getValue("eta_start")),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => getStatusBadge(row.getValue("status")),
    },
    {
      accessorKey: "distance_planned_km",
      header: "Distância (km)",
      cell: ({ row }) => {
        const distance = row.getValue("distance_planned_km") as number;
        return distance ? `${distance} km` : "N/A";
      },
    },
    {
      id: "actions",
      header: "Ações",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEdit(row.original)}
            >
              <Edit2 className="h-4 w-4" />
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => deleteTripMutation.mutate(row.original.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (!currentCompanyId) {
    return (
      <div className="p-6">
        <p>Nenhuma empresa associada encontrada.</p>
      </div>
    );
  }

  const plannedTrips = trips?.filter(t => t.status === "planned")?.length || 0;
  const inProgressTrips = trips?.filter(t => t.status === "in_progress")?.length || 0;
  const completedTrips = trips?.filter(t => t.status === "completed")?.length || 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Viagens</h1>
          <p className="text-muted-foreground">Gerencie viagens e rotas da frota</p>
        </div>
        <div className="flex gap-2 items-center">
          {isViewer && (
            <Badge variant="secondary">
              👁️ Modo Visualização
            </Badge>
          )}
          <Button 
            disabled={!canCreate} 
            onClick={() => {
              setEditingTrip(null);
              setIsDialogOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Nova Viagem
          </Button>

          <TripFormSheet
            open={isDialogOpen}
            onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) setEditingTrip(null);
            }}
            companyId={currentCompanyId}
            mode={editingTrip ? 'edit' : 'create'}
            initialData={editingTrip}
          />
        </div>
      </div>

      <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Viagens Planejadas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{plannedTrips}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Em Andamento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{inProgressTrips}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Concluídas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{completedTrips}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Todas as Viagens</CardTitle>
          <CardDescription>
            Gerencie e acompanhe todas as viagens da frota
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable columns={columns} data={trips || []} />
        </CardContent>
      </Card>
    </div>
  );
}